<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-credit-card-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">Stripe credentials</h2>
        <div class="desc-row">
          <p class="desc">Enter your Stripe keys to enable payments.</p>
        </div>
        <div class="mt-4">
          <v-btn
            variant="tonal"
            color="primary"
            append-icon="mdi-information-outline"
            class="guide-btn"
            @click="openGuide"
          >
            STRIPE SETUP GUIDE
          </v-btn>
        </div>
      </div>
    </div>
    <div v-if="genericError" class="api-error">{{ genericError }}</div>
    <v-form class="form">
      <AppTextField
        v-model="credentialName"
        autocomplete="off"
        label="Payment Gateway"
        label-icon="mdi-tag-outline"
        placeholder="e.g. Primary Stripe Account"
        :error-messages="credentialNameError"
        @update:modelValue="credentialNameError = ''"
      />

      <AppTextField
        v-model="publicKey"
        autocomplete="off"
        label="Publishable key"
        label-icon="mdi-key-outline"
        placeholder="pk_..."
        :error-messages="publicKeyError"
        @update:modelValue="publicKeyError = ''"
      />

      <AppTextField
        v-model="secretKey"
        autocomplete="off"
        label="Secret key"
        label-icon="mdi-lock-outline"
        placeholder="sk_..."
        :error-messages="secretKeyError"
        @update:modelValue="secretKeyError = ''"
      />

      <div v-if="existingWebhookSecret" class="existing-secret">
        <div class="existing-secret__label">Webhook signing secret</div>
        <div class="existing-secret__value">{{ existingWebhookSecret }}</div>
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { useModalStore } from "@/shared/stores/modal";
import StripeCredentialsGuideModal from "@/modules/vendor/components/profile-setup/StripeCredentialsGuideModal.vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, string> | null;
  profileInfo?: any;
}>();

const modal = useModalStore();

const publicKey = ref("");
const secretKey = ref("");
const credentialName = ref("Stripe Account");
const existingWebhookSecret = ref("");

const publicKeyError = ref("");
const secretKeyError = ref("");
const credentialNameError = ref("");
const genericError = ref("");

function getValues() {
  return {
    name: credentialName.value,
    publicKey: publicKey.value,
    secretKey: secretKey.value,
  };
}

defineExpose({ getValues });

function openGuide() {
  modal.open(
    StripeCredentialsGuideModal,
    {},
    { fullscreen: true, title: "Stripe setup guide", showHeader: false }
  );
}

watch(
  () => props.profileInfo,
  (info) => {
    const creds = info?.payment_credentials?.credentials || null;
    if (!creds) return;
    const name = info?.payment_credentials?.name ?? "";

    // Prefill only when the user hasn't typed anything yet.
    if (
      typeof name === "string" &&
      name.trim() &&
      (!credentialName.value || credentialName.value === "Stripe Account")
    ) {
      credentialName.value = name;
    }
    if (!publicKey.value && typeof creds.publishable_key === "string") {
      publicKey.value = creds.publishable_key;
    }
    if (!secretKey.value && typeof creds.secret_key === "string") {
      secretKey.value = creds.secret_key;
    }
    if (typeof creds.webhook_secret === "string" && creds.webhook_secret.trim()) {
      existingWebhookSecret.value = creds.webhook_secret.trim();
    } else if (!info?.payment_credentials) {
      existingWebhookSecret.value = "";
    }
  },
  { immediate: true }
);

function friendlyMessageFor(key: string, raw: string) {
  const msg = String(raw || "");
  // If API indicates required, make a user-friendly message
  if (/required/i.test(msg)) {
    if (/(publishable|public)/i.test(key)) return "Publishable key is required.";
    if (/(secret_key|secret)/i.test(key)) return "Secret key is required.";
  }
  // fallback: return raw message
  return msg;
}

// Map incoming API errors to local fields, including nested keys like credentials.publishable_key
watch(
  () => props.errors,
  (errs: any) => {
    publicKeyError.value = "";
    secretKeyError.value = "";
    credentialNameError.value = "";
    genericError.value = "";
    if (!errs) return;

    Object.entries(errs).forEach(([k, v]) => {
      const raw = Array.isArray(v) ? v.join(" ") : String(v ?? "");
      const keyPart = k.includes(".") ? k.split(".").pop() || k : k;
      const key = String(keyPart);
      const lower = key.toLowerCase();
      const friendly = friendlyMessageFor(lower, raw);

      if (/^name$/i.test(lower)) {
        credentialNameError.value = friendly;
      } else if (/(publishable|public)/i.test(lower)) {
        publicKeyError.value = friendly;
      } else if (/(secret_key|secret)/i.test(lower)) {
        secretKeyError.value = friendly;
      } else {
        genericError.value = genericError.value
          ? `${genericError.value} ${friendly}`
          : friendly;
      }
    });
  },
  { immediate: true }
);
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.desc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.guide-btn {
  text-transform: none;
  font-weight: 700;
}
.form {
  display: grid;
  gap: 20px;
  margin-top: 8px;
}

.hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  /* background: #f9fafb;
  border: 1px solid #e5e7eb; */
  border-radius: var(--radius-md);
  margin-top: -8px;
}

.hint-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.hint-text {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  font-weight: 400;
}

.existing-secret {
  display: grid;
  gap: 6px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: var(--radius-md);
  background: #f9fafb;
  border-radius: var(--radius-lg);
}

.existing-secret__label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.existing-secret__value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 12px;
  color: #111827;
  word-break: break-all;
}

.api-error {
  color: #dc2626; /* red-600 */
  font-size: 13px;
  margin-top: 8px;
}
</style>
